// @ts-nocheck
import fieldSizeToButtonSize from "@store/Framework/Common/Utilities/fieldSizeToButtonSize";
import FlexWrapper, {
    flexWrapperGapStyles,
    FlexWrapperPresentationProps,
} from "@store/Library/Components/Toolkit/FlexWrapper";
import { FormFieldSizeVariant } from "@store/MobiusOverrides/FormField/FormField";
import ProductAddToCartButton, {
    ProductAddToCartButtonProps,
    useShowProductAddToCartButton,
} from "@store/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered, {
    ProductQuantityOrderedProps,
    useShowProductQuantityOrdered,
} from "@store/Overrides/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect, {
    ProductUnitOfMeasureSelectProps,
    useShowProductUnitOfMeasureSelect,
} from "@store/Overrides/Components/ProductUnitOfMeasureSelect";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { ButtonSizeVariant } from "@toolkit/Framework/Types/Button";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
/* Store Custom Code - End */

type ExtendedProps<T> = T & {
    "data-test-selector"?: string;
};

export interface AddToCartOptionsAndButtonProps extends ExpandStyles<AddToCartOptionsAndButtonStyles> {
    sizeVariant?: FormFieldSizeVariant;
    buttonSizeVariant?: ButtonSizeVariant;
    showUnitOfMeasure?: boolean;
}

export interface AddToCartOptionsAndButtonStyles {
    flexWrapper?: FlexWrapperPresentationProps;
    unitOfMeasure?: ExtendedProps<ProductUnitOfMeasureSelectProps>;
    quantityOrdered?: ExtendedProps<ProductQuantityOrderedProps>;
    addToCartButton?: ExtendedProps<ProductAddToCartButtonProps>;
    // Store Custom Code
    multipleSaleQtyWrapper?: InjectableCss;
    multipleSaleQtyTextStyles?: TypographyProps;
}

export const addToCartOptionsAndButtonStyles: AddToCartOptionsAndButtonStyles = {
    flexWrapper: {
        gap: pxToRem(10),
    },
    unitOfMeasure: {
        extendedStyles: {
            cssOverrides: {
                formField: css`
                    flex: 1 1 50%;
                    width: auto;
                `,
            },
        },
    },
    addToCartButton: {
        extendedStyles: {
            button: {
                css: css`
                    flex: 1 0 auto;
                    align-self: flex-end;
                `,
            },
        },
    },
    /* Store Custom Code - Start */
    quantityOrdered: {
        extendedStyles: {
            labelProps: {
                css: css`
                    /* important needed on "font-weight" to bypass problematic mobius merge */
                    font-weight: ${x => x.theme.design.typography.weight.base} !important;
                `,
            },
        },
    },
    multipleSaleQtyWrapper: {
        css: css`
            line-height: normal;
        `,
    },
    multipleSaleQtyTextStyles: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.tiny};
        `,
    },
    /* Store Custom Code - End */
};

const AddToCartOptionsAndButton: FC<AddToCartOptionsAndButtonProps> = ({
    extendedStyles,
    overridedStyles,
    sizeVariant = "small",
    buttonSizeVariant: baseButtonSizeVariant,
    showUnitOfMeasure = true,
}) => {
    /* Store Custom Code - Start */
    const productState = useAppSelector(
        state => getProductState(state, state.pages.productDetails.selectedProductId).value,
    );
    /* Store Custom Code - End */
    const baseStyles = useExpandProps(addToCartOptionsAndButtonStyles, extendedStyles, overridedStyles);
    const styles = useMemo(() => {
        const buttonSizeVariant = baseButtonSizeVariant || fieldSizeToButtonSize(sizeVariant);
        const itemCss = flexWrapperGapStyles(baseStyles.flexWrapper?.gap, baseStyles.flexWrapper?.verticalGap);

        return mergeProps(baseStyles, {
            unitOfMeasure: {
                extendedStyles: {
                    sizeVariant,
                    cssOverrides: {
                        formField: itemCss,
                    },
                },
            },
            quantityOrdered: {
                extendedStyles: {
                    sizeVariant,
                    cssOverrides: {
                        formField: itemCss,
                    },
                },
            },
            addToCartButton: {
                extendedStyles: {
                    button: {
                        sizeVariant: buttonSizeVariant,
                        css: itemCss,
                    },
                },
            },
        } as AddToCartOptionsAndButtonStyles);
    }, [baseStyles, sizeVariant]);

    const showProductAddToCartButton = useShowProductAddToCartButton(
        extendedStyles?.addToCartButton?.configurationCompleted,
        extendedStyles?.addToCartButton?.variantSelectionCompleted,
    );
    const showProductQuantityOrdered = useShowProductQuantityOrdered(
        extendedStyles?.quantityOrdered?.configurationCompleted,
        extendedStyles?.quantityOrdered?.variantSelectionCompleted,
    );
    const showProductUnitOfMeasureSelect = useShowProductUnitOfMeasureSelect();

    if (!showProductAddToCartButton && !showProductQuantityOrdered && !showProductUnitOfMeasureSelect) {
        return null;
    }

    return (
        <>
            {productState?.canAddToCart /* Store Custom Code */ && (
                <>
                    <FlexWrapper wrap={false} {...styles.flexWrapper}>
                        {showUnitOfMeasure && <ProductUnitOfMeasureSelect {...styles.unitOfMeasure} />}
                        <ProductQuantityOrdered {...styles.quantityOrdered} />
                        <ProductAddToCartButton {...styles.addToCartButton} />
                    </FlexWrapper>
                    {/* Store Custom Code - Start */}
                    {productState?.detail?.multipleSaleQty && productState?.detail?.multipleSaleQty > 0 ? (
                        <StyledWrapper {...styles.multipleSaleQtyWrapper}>
                            <Typography {...styles.multipleSaleQtyTextStyles}>
                                {translate("Multiples of {0}", productState?.detail?.multipleSaleQty.toString())}
                            </Typography>
                        </StyledWrapper>
                    ) : null}
                    {/* Store Custom Code - End */}
                </>
            )}
        </>
    );
};

export default AddToCartOptionsAndButton; /* Store Custom Code */
